/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faBriefcase,
  faUser,
  faSignOutAlt,
  faChartLine,
  faBell,
  faEnvelope,
  faFileAlt,
  faUpload,
  faNewspaper,
  faInfoCircle,
  faAngleDown,
  faBars,
  faTimes,
  faCrown,
  faGift,
  faSearch,
  faClipboardList,
  faRocket,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import ModernProfileDropdown from './ModernProfileDropdown';
import akLogo from '../assets/FINAL LOGO AK.png';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);

  const { user, isAuthenticated, logout, forceLogout } = useAuth();
  const { 
    notifications, 
    messages, 
    unreadNotificationCount, 
    unreadMessageCount,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    clearAllNotifications,
    markMessageAsRead,
    isWebSocketConnected
  } = useNotifications();
  
  const location = useLocation();
  const notificationsRef = useRef(null);
  const messagesRef = useRef(null);
  
  // Debug: Log authentication state to see what's available
  useEffect(() => {
    console.log('🔐 Header - Authentication State Changed:', {
      isAuthenticated,
      user: user ? {
        role: user.role,
        userId: user.userId,
        email: user.email,
        hasToken: !!user.token
      } : null,
      location: location.pathname,
      timestamp: new Date().toISOString()
    });
  }, [isAuthenticated, user, location.pathname]);


  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };


  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Get user role from context
  const getUserRole = () => {
    if (!user?.role || user.role === 'undefined') {
      return 'jobSeeker';
    }
    return user.role;
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  // Format time for display
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (messagesRef.current && !messagesRef.current.contains(event.target)) {
        setShowMessages(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isActiveRoute = (path) => {
    return location.pathname === path;
  };


  const getDashboardRoute = () => {
    if (!user || !user.role) return '/';
    
    const role = user.role.toLowerCase();
    if (role === 'recruiter') return '/recruiter-dashboard';
    if (role === 'jobseeker' || role === 'jobseeker') return '/jobseeker-dashboard';
    if (role === 'admin') return '/admin';
    
    return '/';
  };

  return (
    <header className="header">
      
      <div className="header-container">
        {/* Logo */}
        <Link to={getDashboardRoute()} className="header-logo">
          <img src={akLogo} alt="AksharJobs logo" />
          <div>
            <div className="header-logo-text">JOBSEEKER HUB</div>
            <div className="header-logo-tagline">Where Opportunity Meets Talent</div>
          </div>
        </Link>

        {/* Desktop Navigation - Clean primary links */}
        <nav className="header-nav">
          <Link to="/" className={isActiveRoute('/') ? 'active' : ''}>
            <FontAwesomeIcon icon={faHome} />
            Home
          </Link>
          <Link to="/associations" className={isActiveRoute('/associations') ? 'active' : ''}>
            <FontAwesomeIcon icon={faUsers} />
            Association
          </Link>
          <Link to="/universities" className={isActiveRoute('/universities') ? 'active' : ''}>
            <FontAwesomeIcon icon={faHome} />
            Universities
          </Link>
          <Link to="/communities" className={isActiveRoute('/communities') ? 'active' : ''}>
            <FontAwesomeIcon icon={faUsers} />
            Communities
          </Link>
          <Link to="/volunteers" className={isActiveRoute('/volunteers') ? 'active' : ''}>
            <FontAwesomeIcon icon={faUsers} />
            Volunteers
          </Link>
          <Link to="/professional" className={isActiveRoute('/professional') ? 'active' : ''}>
            <FontAwesomeIcon icon={faUser} />
            Professionals
          </Link>
          <Link to="/interns" className={isActiveRoute('/interns') ? 'active' : ''}>
            <FontAwesomeIcon icon={faRocket} />
            Interns
          </Link>
          <Link to="/jobs" className={isActiveRoute('/jobs') ? 'active' : ''}>
            <FontAwesomeIcon icon={faBriefcase} />
            Find Jobs
          </Link>
          <Link to="/modern-upload" className={isActiveRoute('/modern-upload') ? 'active' : ''}>
            <FontAwesomeIcon icon={faFileAlt} />
            CV Builder
          </Link>
          <Link to="/contact" className={isActiveRoute('/contact') ? 'active' : ''}>
            <FontAwesomeIcon icon={faEnvelope} />
            Contact
          </Link>
          <Link to="/blog" className={isActiveRoute('/blog') ? 'active' : ''}>
            <FontAwesomeIcon icon={faNewspaper} />
            Blog
          </Link>
          <Link to="/about-platform" className={isActiveRoute('/about-platform') ? 'active' : ''}>
            <FontAwesomeIcon icon={faInfoCircle} />
            About platform
          </Link>
          <Link to="/associations" className={isActiveRoute('/associations') ? 'active' : ''}>
            <FontAwesomeIcon icon={faUsers} />
            Association
          </Link>
          <Link to="/post-job" className={isActiveRoute('/post-job') ? 'active' : ''}>
            <FontAwesomeIcon icon={faUpload} />
            Post a Job
          </Link>
        </nav>

        {/* Header Actions */}
        <div className="header-actions">
          {isAuthenticated ? (
            <>
              {/* Notifications */}
              <div className="notifications-wrapper" ref={notificationsRef}>
            <button 
              className="action-button notifications-btn" 
              onClick={() => setShowNotifications(!showNotifications)}
              aria-label="Toggle notifications"
            >
              <FontAwesomeIcon icon={faBell} />
              {unreadNotificationCount > 0 && (
                <span className="notification-badge">{unreadNotificationCount}</span>
              )}
            </button>
            
            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="notifications-dropdown show">
                <div className="notifications-header">
                  <h3>Notifications ({unreadNotificationCount} unread)</h3>
                  <div className="notifications-actions">
                    <button 
                      className="mark-all-read" 
                      onClick={markAllNotificationsAsRead}
                      disabled={unreadNotificationCount === 0}
                    >
                      Mark all read
                    </button>
                    <button 
                      className="clear-all" 
                      onClick={clearAllNotifications}
                      disabled={notifications.length === 0}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div className="notifications-list">
                  {notifications.length === 0 ? (
                    <div className="no-notifications">
                      <p>No notifications yet</p>
                    </div>
                  ) : (
                    notifications
                      .filter(notification => !notification.is_read && !notification.read) // Only show unread notifications
                      .map(notification => (
                      <div 
                        key={notification.id || notification._id} 
                        className={`notification-item ${!notification.is_read && !notification.read ? 'unread' : ''}`}
                        onClick={() => markNotificationAsRead(notification.id || notification._id)}
                      >
                        <div className="notification-icon">
                          <FontAwesomeIcon 
                            icon={
                              notification.type === 'job' ? faBriefcase :
                              notification.type === 'message' ? faEnvelope :
                              notification.type === 'application' ? faFileAlt :
                              faBell
                            } 
                          />
                        </div>
                        <div className="notification-content">
                          <h4>{notification.title}</h4>
                          <p>{notification.message}</p>
                          <span className="notification-time">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="notifications-footer">
                  <Link to="/notifications" className="view-all-notifications">
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>
          
          {/* Messages */}
          <div className="messages-wrapper" ref={messagesRef}>
            <button 
              className="action-button messages-btn" 
              onClick={() => setShowMessages(!showMessages)}
              aria-label="Toggle messages"
            >
              <FontAwesomeIcon icon={faEnvelope} />
              {unreadMessageCount > 0 && (
                <span className="message-badge">{unreadMessageCount}</span>
              )}
            </button>
            
            {/* Messages Dropdown */}
            {showMessages && (
              <div className="messages-dropdown">
                <div className="messages-header">
                  <h3>Messages</h3>
                  <div className="messages-actions">
                    <button 
                      className="mark-all-read" 
                      onClick={() => setShowMessages(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
                <div className="messages-list">
                  {messages.length === 0 ? (
                    <div className="no-messages">
                      <p>No messages yet</p>
                    </div>
                  ) : (
                    messages.slice(0, 5).map(message => (
                      <div 
                        key={message.id} 
                        className={`message-item ${!message.is_read ? 'unread' : ''}`}
                        onClick={() => markMessageAsRead(message.id)}
                      >
                        <div className="message-icon">
                          <FontAwesomeIcon icon={faEnvelope} />
                        </div>
                        <div className="message-content">
                          <h4>
                            {message.is_sent ? 'You' : 'New Message'}
                          </h4>
                          <p>{message.content}</p>
                          <span className="message-time">
                            {formatTime(message.created_at)}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="messages-footer">
                  <Link to="/messages" className="view-all-messages">
                    View all messages
                  </Link>
                </div>
              </div>
            )}
          </div>
          
          {/* Modern Profile Dropdown */}
          <ModernProfileDropdown />
            </>
          ) : (
            /* Unauthenticated User Actions */
            <div className="auth-actions">
              <Link to="/login" className="auth-button login-btn">
                SIGN IN
              </Link>
              <Link to="/signup" className="auth-button signup-btn">
                GET STARTED
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMobileMenuOpen ? 'show' : ''}`}>
        <div className="mobile-menu-content">
          {/* Main Navigation */}
          <div className="mobile-menu-section">
            <div className="mobile-menu-title">Navigation</div>
            <div className="mobile-menu-links">
              <Link to="/" className="mobile-menu-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faHome} />
                Home
              </Link>
              <Link to="/premium" className="mobile-menu-link premium-mobile-nav" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faCrown} />
                GET PREMIUM
              </Link>
              <Link to="/jobs" className="mobile-menu-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faBriefcase} />
                Browse Jobs
              </Link>
              <Link to="/about-platform" className="mobile-menu-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faInfoCircle} />
                About Platform
              </Link>
              <Link to="/associations" className="mobile-menu-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faUsers} />
                Association
              </Link>
              <Link to="/interns" className="mobile-menu-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faRocket} />
                Interns
              </Link>
              <Link to="/blog" className="mobile-menu-link" onClick={closeMobileMenu}>
                <FontAwesomeIcon icon={faNewspaper} />
                Blog
              </Link>
            </div>
          </div>

          {/* User Actions */}
          {isAuthenticated ? (
            <div className="mobile-menu-section">
              <div className="mobile-menu-title">Account</div>
              <div className="mobile-menu-links">
                {getUserRole() === 'recruiter' && (
                  <Link to="/post-job" className="mobile-menu-link" onClick={closeMobileMenu}>
                    <FontAwesomeIcon icon={faUpload} />
                    Post Job
                  </Link>
                )}
                {getUserRole() === 'jobSeeker' && (
                  <>
                    <Link to="/jobs" className="mobile-menu-link" onClick={closeMobileMenu}>
                      <FontAwesomeIcon icon={faBriefcase} />
                      Browse Jobs
                    </Link>
                                        <Link to="/modern-upload" className="mobile-menu-link upload-resume-mobile" onClick={closeMobileMenu}>
                      <FontAwesomeIcon icon={faFileAlt} />
                      Upload Resume
                      <span className="mobile-ai-badge">AI</span>
                    </Link>
                  </>
                )}
                <button onClick={handleLogout} className="mobile-menu-link">
                  <FontAwesomeIcon icon={faSignOutAlt} />
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <div className="mobile-menu-section">
              <div className="mobile-menu-title">Account</div>
              <div className="mobile-menu-links">
                <Link to="/login" className="mobile-menu-link" onClick={closeMobileMenu}>
                  <FontAwesomeIcon icon={faUser} />
                  SIGN IN
                </Link>
                <Link to="/signup" className="mobile-menu-link" onClick={closeMobileMenu}>
                  <FontAwesomeIcon icon={faUser} />
                  GET STARTED
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Network Status Component removed on request */}
      
      {/* Connection Status Component */}
    </header>
  );
};

export default Header;
